import { useEffect, useMemo, useRef } from "react";
import {
  buildCartUpsellDiscountSyncFingerprint,
  cartDrawerUpsellDiscountNeedsSync,
  syncCartDrawerUpsellDiscount,
} from "../../shared/cart-drawer-upsell-discount.js";
import cartService from "../services/cart.service";

/**
 * When cart-drawer upsell trigger rules change, revoke or restore popup discounts
 * on lines originally added from the cart-drawer upsell strip only.
 */
export function useCartDrawerUpsellDiscountSync({
  popup,
  cart,
  enabled = true,
  onCartUpdated,
}) {
  const syncingRef = useRef(false);
  const lastAttemptFingerprintRef = useRef("");

  const cartFingerprint = useMemo(
    () => buildCartUpsellDiscountSyncFingerprint(cart, popup),
    [cart, popup]
  );

  useEffect(() => {
    if (!enabled || !popup || !cart || syncingRef.current) return;

    if (!cartDrawerUpsellDiscountNeedsSync(popup, cart)) {
      lastAttemptFingerprintRef.current = "";
      return;
    }

    if (lastAttemptFingerprintRef.current === cartFingerprint) return;

    syncingRef.current = true;
    lastAttemptFingerprintRef.current = cartFingerprint;

    const shop =
      typeof window !== "undefined"
        ? window?.Shopify?.shop || window.location.hostname
        : "";

    (async () => {
      try {
        const updatedCart = await syncCartDrawerUpsellDiscount({
          popup,
          cart,
          changeLineItem: (payload) => cartService.changeLineItem(payload),
          shop,
        });

        if (updatedCart) {
          onCartUpdated?.(updatedCart);
        }
      } catch (err) {
        console.error(
          "[CartDrawerUpsell] Failed to sync upsell discount tracking:",
          err
        );
      } finally {
        syncingRef.current = false;
      }
    })();
  }, [popup, cartFingerprint, enabled, onCartUpdated]);
}
